/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CloseOutlined } from '@ant-design/icons'
import { Alert, App, Checkbox, Modal } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useState, useEffect, useMemo } from 'react'
import { useModalScroll } from 'src/hooks/useModalScroll'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import Button from 'src/components/ts-components/Button'
import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import CurrencyInput from 'src/components/ts-components/CurrencyInput'
import { TextInput } from 'src/components/ts-components/TextInput'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import DatePicker from 'src/components/ts-components/DatePicker'
import dayjs from 'dayjs'

const styles = css`
  .my-modal-body {
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`

const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10002,
  },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10001,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

type NewPaymentModalProps = {
  isOpen: boolean
  onClose: () => void
  refetch: (shouldRefresh: boolean) => void
  currency?: string | null | undefined
}

const paymentTypes = [
  { label: 'Other', value: 'other' },
  { label: 'Deposit', value: 'deposit' },
  { label: 'Installment', value: 'installment' },
]

const NewPaymentModal = ({
  isOpen,
  onClose,
  refetch,
}: NewPaymentModalProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const { message } = App.useApp()
  const { openModal, closeModal } = useModalScroll()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmChecked, setConfirmChecked] = useState(false)
  const [description, setDescription] = useState('')
  const [paymentType, setPaymentType] = useState('other')
  const [amount, setAmount] = useState('0')
  const [dueDate, setDueDate] = useState<dayjs.Dayjs | null>(dayjs())

  const handleCreatePayment = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      await req('/billing/schedule', {
        method: 'POST',
        body: {
          description,
          payment_type: paymentType,
          amount: parseFloat(amount),
          due_date: dueDate?.format('YYYY-MM-DD'),
        },
      })
      message.success('Payment created successfully')
      onClose()
      refetch(false)
    } catch (e: any) {
      const errorMessage = getErrorMessage(e)
      setError(errorMessage || 'Failed to create payment')
    } finally {
      setIsLoading(false)
    }
  }, [amount, description, dueDate, message, onClose, paymentType, refetch])

  const handleCancel = useCallback(() => {
    onClose()
  }, [onClose])

  useEffect(() => {
    if (isOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isOpen, openModal])

  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: ${isMobile ? '100%' : '50%'};
          margin-left: ${isMobile ? '0' : 'auto'};
          @media (max-width: 768px) {
            width: 100%;
            flex-direction: column;
          }
        `}
      >
        <Button text='Cancel' onClick={handleCancel} variant='secondary' />
        <Button
          text={isLoading ? 'Creating...' : 'Create'}
          onClick={handleCreatePayment}
          variant='primary'
          disabled={isLoading || !confirmChecked}
        />
      </div>
    )
  }, [handleCancel, handleCreatePayment, isLoading, isMobile, confirmChecked])

  const getHeader = useCallback(() => {
    return (
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        Request New Payment
      </Text>
    )
  }, [])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          padding: 12px;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        background: ${colors.SECONDARY_400} !important;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '40vw'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '8px'};
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
          background: ${colors.SECONDARY_300};
        }
      `}
      width={isMobile ? '100%' : '40vw'}
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={getFooter()}
      open={isOpen}
      onCancel={handleCancel}
      centered={!isMobile}
      maskClosable={false}
      classNames={classNames}
      styles={modalStyles}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 0 8px;
        `}
      >
        <Text variant='paragraphMedium' color={colors.NEUTRAL_0}>
          Fill in the payment details below to request payment from the player:
        </Text>

        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          <TextInput
            name='description'
            value={description}
            setValue={setDescription}
            label='Description'
            placeholder='Enter payment description'
            fieldErrors={[]}
            customError={{}}
          />

          <SelectInput
            label='Type'
            disabled={false}
            options={paymentTypes}
            name='paymentType'
            value={paymentType}
            onChange={setPaymentType}
            fieldErrors={[]}
            customError={{}}
          />

          <div
            css={css`
              display: flex;
              gap: 16px;
              width: 100%;
            `}
          >
            <div
              css={css`
                flex: 1;
              `}
            >
              <CurrencyInput
                label='Amount'
                value={parseFloat(amount)}
                onChange={(value) => setAmount(value?.toString() || '0')}
                name='amount'
              />
            </div>
            <div
              css={css`
                flex: 1;
              `}
            >
              <DatePicker
                label='Date'
                value={dueDate}
                onChange={(date) => setDueDate(date)}
              />
            </div>
          </div>
        </div>

        <Checkbox
          checked={confirmChecked}
          onChange={(e) => setConfirmChecked(e.target.checked)}
          css={css`
            .ant-checkbox-inner {
              background-color: transparent;
              border-color: ${colors.WHITE};
            }
            .ant-checkbox-checked .ant-checkbox-inner {
              background-color: ${colors.PRIMARY};
              border-color: ${colors.PRIMARY};
            }
          `}
        >
          <Text variant='paragraphSmall' color={colors.WHITE} weight='regular'>
            I confirm that the details entered above are accurate.
          </Text>
        </Checkbox>
        {error && <Alert message={error} type='error' showIcon />}
      </div>
    </Modal>
  )
}

export default NewPaymentModal
